/**
 * CounterPaymentPage — `/mail/payments/counter`.
 *
 * Ported from `screens/src/pages/mail/CounterPayment.jsx`. Records a counter
 * payment intent against a mail draft / item and lets the clerk capture or
 * void it.
 *
 * Backend wiring (live):
 *   - POST /v1/payments      — body: { reference: { kind, id? }, amount, currency, methodKind }
 *   - POST /v1/payments/:id/capture
 *   - POST /v1/payments/:id/void
 *
 * DTO: the backend expects `amount` as a decimal NGN string (e.g. "500.00")
 * and `methodKind` (not `method`). `reference` is a domain-link object with
 * at least a `kind` field. See service-payment/src/modules/payment/payment.dto.ts.
 */
import { type JSX, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { CreditCard } from 'lucide-react';

import {
  capturePayment,
  createPayment,
  voidPayment,
  type PaymentMethod,
  type PaymentSummary,
} from '../../lib/api/payments';
import { PageHeader } from '../shared/StaffUI';

// POS is not in the backend's PaymentMethodKindSchema; use the backend-supported set.
const METHODS: readonly PaymentMethod[] = ['CASH', 'CARD', 'TRANSFER', 'WALLET'];

export function CounterPaymentPage(): JSX.Element {
  const [amount, setAmount] = useState<string>('');
  const [methodKind, setMethodKind] = useState<PaymentMethod>('CASH');
  const [referenceId, setReferenceId] = useState<string>('');
  const [created, setCreated] = useState<PaymentSummary | null>(null);

  const create = useMutation({
    mutationFn: () => {
      // `amount` is entered by the clerk in NGN (e.g. "500" or "500.50").
      // Backend expects a decimal string with up to 4dp — format to 2dp.
      const amountStr = parseFloat(amount || '0').toFixed(2);
      return createPayment({
        reference: {
          kind: 'MAIL_COUNTER',
          ...(referenceId.trim() !== '' && { id: referenceId.trim() }),
        },
        amount: amountStr,
        currency: 'NGN',
        methodKind,
      });
    },
    onSuccess: (p) => setCreated(p),
  });

  const capture = useMutation({
    mutationFn: (id: string) => capturePayment(id),
    onSuccess: (p) => setCreated(p),
  });

  const voidIt = useMutation({
    mutationFn: (id: string) => voidPayment(id),
    onSuccess: (p) => setCreated(p),
  });

  return (
    <div className="space-y-5">
      <PageHeader
        title="Counter Payment"
        description="Take a payment against a mail draft / item at the counter."
      />

      <div className="bg-white border border-border rounded-xl p-5">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2 mb-3">
          <CreditCard className="w-4 h-4 text-nipost-green" /> New payment
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label htmlFor="cp-amt" className="text-xs font-semibold text-muted uppercase">Amount (NGN)</label>
            <input
              id="cp-amt"
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-lg"
            />
          </div>
          <div>
            <label htmlFor="cp-method" className="text-xs font-semibold text-muted uppercase">Method</label>
            <select
              id="cp-method"
              value={methodKind}
              onChange={(e) => setMethodKind(e.target.value as PaymentMethod)}
              className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-lg bg-white"
            >
              {METHODS.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="cp-ref" className="text-xs font-semibold text-muted uppercase">Reference</label>
            <input
              id="cp-ref"
              type="text"
              value={referenceId}
              onChange={(e) => setReferenceId(e.target.value)}
              placeholder="Tracking number, draft id, etc."
              className="mt-1 w-full px-3 py-2 text-sm border border-border rounded-lg"
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 mt-4">
          {create.isError ? (
            <span className="text-xs text-red-700">{(create.error as Error).message}</span>
          ) : null}
          <button
            type="button"
            disabled={amount.trim() === '' || create.isPending}
            onClick={() => create.mutate()}
            className="px-4 py-2 text-sm font-semibold rounded-lg bg-nipost-green text-white hover:bg-nipost-green-dark disabled:opacity-50"
          >
            {create.isPending ? 'Creating…' : 'Create payment'}
          </button>
        </div>
      </div>

      {created ? (
        <div className="bg-white border border-border rounded-xl p-5">
          <h3 className="font-semibold text-gray-900 mb-3">Payment</h3>
          <dl className="text-sm grid grid-cols-2 gap-2">
            <dt className="text-muted text-xs">Id</dt>
            <dd className="font-mono text-xs">{created.id}</dd>
            <dt className="text-muted text-xs">Amount</dt>
            <dd className="font-mono">₦{parseFloat(created.amountAuthorized).toLocaleString('en-NG', { minimumFractionDigits: 2 })}</dd>
            <dt className="text-muted text-xs">Method</dt>
            <dd>{created.methodKind}</dd>
            <dt className="text-muted text-xs">Status</dt>
            <dd className="font-semibold">{created.status}</dd>
          </dl>
          <div className="flex gap-2 mt-3">
            <button
              type="button"
              disabled={capture.isPending || (created.status !== 'AUTHORISED' && created.status !== 'AUTHORIZED')}
              onClick={() => capture.mutate(created.id)}
              className="px-3 py-1.5 bg-nipost-green text-white text-xs font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
            >
              Capture
            </button>
            <button
              type="button"
              disabled={voidIt.isPending || created.status === 'CAPTURED'}
              onClick={() => voidIt.mutate(created.id)}
              className="px-3 py-1.5 border border-red-300 text-red-700 text-xs font-semibold rounded-lg hover:bg-red-50 disabled:opacity-50"
            >
              Void
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
